import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import { ToastProvider } from './components/Toast/ToastContainer';
import { AbilityProvider } from './contexts/AbilityContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './pages/Login';
import Logout from './pages/Logout';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SalesDashboard from './pages/SalesDashboard';
import Customers from './pages/Customers';
import Transportes from './pages/Transportes';
import Products from './pages/Products';
import TipoCaja from './pages/TipoCaja';
import Orders from './pages/Orders';
import RecurringOrders from './pages/RecurringOrders';
import Inventory from './pages/Inventory';
import InventoryHistory from './pages/InventoryHistory';
import PlanCosecha from './pages/PlanCosecha';
import DespachoNacionales from './pages/DespachoNacionales';
import OneDriveCallback from './pages/OneDriveCallback';
import M365EmailCallback from './pages/M365EmailCallback';
import Configuracion from './pages/Configuracion';
import ConfiguracionOneDrive from './pages/ConfiguracionOneDrive';
import ConfiguracionIntegraciones from './pages/ConfiguracionIntegraciones';
import ConfiguracionEmail from './pages/ConfiguracionEmail';
import ConfigPedidosConfirmados from './pages/ConfigPedidosConfirmados';
import FolioManagement from './pages/FolioManagement';
import RoleManagement from './pages/Admin/RoleManagement';

// Lazy load heavy components (Handsontable pages)
const ExportPreparativos = lazy(() => import('./pages/ExportPreparativos'));
const PreparativosDocumentos = lazy(() => import('./pages/PreparativosDocumentos'));
const ExportEmbarque = lazy(() => import('./pages/ExportEmbarque'));
const EmbarqueTaskOverview = lazy(() => import('./pages/EmbarqueTaskOverview'));
const FacturaExportacion = lazy(() => import('./pages/FacturaExportacion'));
const FacturaLocal = lazy(() => import('./pages/FacturaLocal'));
const FacturaProformaFinal = lazy(() => import('./pages/FacturaProformaFinal'));
const PesoGross = lazy(() => import('./pages/PesoGross'));
const CostosEstimados = lazy(() => import('./pages/CostosEstimados'));
const TarifasAnuales = lazy(() => import('./pages/TarifasAnuales'));
const TipoCambio = lazy(() => import('./pages/TipoCambio'));
const Presupuesto = lazy(() => import('./pages/Presupuesto'));
const PresupuestoArchivos = lazy(() => import('./pages/PresupuestoArchivos'));
const Produccion = lazy(() => import('./pages/Produccion'));
const ProduccionArchivos = lazy(() => import('./pages/ProduccionArchivos'));
const Gastos = lazy(() => import('./pages/Gastos'));
const GastosArchivos = lazy(() => import('./pages/GastosArchivos'));
const ReporteDatosHistoricosEmbarque = lazy(() => import('./pages/ReporteDatosHistoricosEmbarque'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#2c5f2d'
  }}>
    <div>
      <div style={{ marginBottom: '10px' }}>Cargando...</div>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #2c5f2d',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto'
      }} />
    </div>
  </div>
);

function App() {
  return (
    <ToastProvider>
      <AbilityProvider>
        <div className="App">
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Routes - Wrapped in Layout */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Navigate to="/dashboard" replace />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <SalesDashboard />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/customers" element={
            <ProtectedRoute>
              <Layout>
                <Customers />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/customers/transportes" element={
            <ProtectedRoute>
              <Layout>
                <Transportes />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/products" element={
            <ProtectedRoute>
              <Layout>
                <Products />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/products/tipo-caja" element={
            <ProtectedRoute>
              <Layout>
                <TipoCaja />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/orders" element={
            <ProtectedRoute>
              <Layout>
                <Orders />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/orders/recurring" element={
            <ProtectedRoute>
              <Layout>
                <RecurringOrders />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/inventory" element={
            <ProtectedRoute>
              <Layout>
                <Inventory />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/inventory/history" element={
            <ProtectedRoute>
              <Layout>
                <InventoryHistory />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/plan-cosecha" element={
            <ProtectedRoute>
              <Layout>
                <PlanCosecha />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/preparativos" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ExportPreparativos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/preparativos/:stepKey" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ExportPreparativos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/preparativos/documentos" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PreparativosDocumentos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <EmbarqueTaskOverview />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/factura-exportacion" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <FacturaExportacion />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/export/embarque/factura-local" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <FacturaLocal />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/export/embarque/factura-proforma-final" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <FacturaProformaFinal />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/peso-gross" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PesoGross />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/costos-estimados" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <CostosEstimados />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/tarifas-anuales" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <TarifasAnuales />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/tipo-cambio" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <TipoCambio />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/export/embarque/stepper" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ExportEmbarque />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/despacho-nacionales" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'despacho-nacionales:view' }}>
              <Layout>
                <DespachoNacionales />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/reportes/datos-historicos-embarque" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'reportes:datos-historicos-embarque:view' }}>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ReporteDatosHistoricosEmbarque />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/presupuesto" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Presupuesto />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/presupuesto/archivos" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'budget:files' }}>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PresupuestoArchivos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/produccion" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Produccion />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/produccion/archivos" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'production:files' }}>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ProduccionArchivos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/gastos" element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Gastos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/gastos/archivos" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'expenses:files' }}>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <GastosArchivos />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config' }}>
              <Layout>
                <Configuracion />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion/onedrive" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config' }}>
              <Layout>
                <ConfiguracionOneDrive />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion/integraciones" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config' }}>
              <Layout>
                <ConfiguracionIntegraciones />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion/pedidos-confirmados" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config' }}>
              <Layout>
                <ConfigPedidosConfirmados />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion/correo" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config:correo' }}>
              <Layout>
                <ConfiguracionEmail />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion/folios" element={
            <ProtectedRoute requiredPermission={{ action: 'read', subject: 'config' }}>
              <Layout>
                <FolioManagement />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* OneDrive callback - public route (needed for OAuth flow) */}
          <Route path="/auth/onedrive/callback" element={<OneDriveCallback />} />
          
          {/* M365 Email callback - public route (needed for OAuth flow) */}
          <Route path="/auth/m365-email/callback" element={<M365EmailCallback />} />
          
          {/* Admin Routes */}
          <Route path="/admin/roles" element={
            <ProtectedRoute requiredPermission={{ action: 'manage', subject: 'all' }}>
              <Layout>
                <RoleManagement />
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
        </div>
      </AbilityProvider>
    </ToastProvider>
  );
}

export default App;
